import { useEffect, useState } from 'react';
import { Address, AddressInput } from '@teko/user';
import { useAuth } from '@teko/auth-react';

const handleSetDefault = (
  myAddresses: Array<Address>,
  newAddress: AddressInput,
) => {
  return newAddress.isDefault
    ? [
        ...myAddresses.map((addr: Address) =>
          addr.isDefault ? { ...addr, isDefault: false } : addr,
        ),
        newAddress,
      ]
    : [...myAddresses, newAddress];
};

export const useAddresses = () => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, userService } = useAuth();

  const fetchAddresses = async () => {
    setLoading(true);
    return userService
      .getMyAddresses()
      .then((_addresses) => {
        setAddresses(_addresses);
        return _addresses;
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (user?.userId) fetchAddresses();
  }, [user?.userId]);

  const addAddress = async (addr: AddressInput) => {
    setLoading(true);
    // add to cache first
    setAddresses(handleSetDefault(addresses, addr));
    // actually call add
    // and then update cache
    const newAddr = await userService.createAddress(addr);
    setAddresses(handleSetDefault(addresses, newAddr));
    setLoading(false);
    return newAddr;
  };

  const updateAddress = async (updateAddress: Address) => {
    if (!updateAddress.addressId) return;
    setLoading(true);
    setAddresses(
      addresses.map((address: Address) =>
        address.addressId === updateAddress.addressId
          ? updateAddress
          : updateAddress.isDefault
          ? { ...address, isDefault: false }
          : address,
      ),
    );
    await userService.updateAddress(updateAddress.addressId, updateAddress);
    setLoading(false);
    return updateAddress;
  };

  const deleteAddress = async (addressId: string) => {
    setLoading(true);
    setAddresses(
      addresses.filter((address: Address) => address.addressId !== addressId),
    );
    await userService.deleteAddress(addressId);
    setLoading(false);
  };

  const setAddressDefault = async (addressId: string) => {
    const address = addresses.find(
      (item: Address) => item.addressId === addressId,
    );

    if (address) {
      await updateAddress({ ...address, isDefault: true });
    }
  };

  return {
    addresses,
    setAddresses,
    updateAddress,
    fetchAddresses,
    deleteAddress,
    addAddress,
    setAddressDefault,
    loading,
  };
};
